// Export helpers for CSV and PDF (browser-native, no dependencies).

function csvEscape(s: string): string {
  if (s == null) return "";
  const v = String(s);
  // RFC 4180: wrap in double quotes if contains comma, quote, or newline; double up internal quotes.
  if (/[",\n\r]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

export interface CsvColumn<T> {
  header: string;
  value: (row: T) => string | number | null | undefined;
}

export function exportCsv<T>(rows: T[], columns: CsvColumn<T>[], filename: string) {
  const header = columns.map((c) => csvEscape(c.header)).join(",");
  const lines = rows.map((r) =>
    columns.map((c) => {
      const v = c.value(r);
      return csvEscape(v == null ? "" : typeof v === "number" ? String(v) : v);
    }).join(",")
  );
  // BOM so Excel opens UTF-8 correctly with non-ASCII chars
  const csv = "﻿" + header + "\n" + lines.join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  downloadBlob(blob, filename.endsWith(".csv") ? filename : `${filename}.csv`);
}

// Combine multiple sheets-worth of CSV into one .csv file with section headers.
// Excel users can open as one sheet; Google Sheets handles it the same way.
export interface CsvSection {
  title: string;
  csv: string;
}
export function exportCsvBundle(sections: CsvSection[], filename: string) {
  const parts: string[] = ["﻿"];
  sections.forEach((s, i) => {
    if (i > 0) parts.push("\n\n");
    parts.push(`# ${s.title}\n`);
    parts.push(s.csv);
  });
  const blob = new Blob([parts.join("")], { type: "text/csv;charset=utf-8;" });
  downloadBlob(blob, filename.endsWith(".csv") ? filename : `${filename}.csv`);
}

// Build a CSV string for one section, returns string only (does not download).
export function buildCsv<T>(rows: T[], columns: CsvColumn<T>[]): string {
  const header = columns.map((c) => csvEscape(c.header)).join(",");
  const lines = rows.map((r) =>
    columns.map((c) => {
      const v = c.value(r);
      return csvEscape(v == null ? "" : typeof v === "number" ? String(v) : v);
    }).join(",")
  );
  return header + "\n" + lines.join("\n");
}

// Trigger the browser's print dialog. The user can choose "Save as PDF"
// in the destination picker — this is the standard board-pack export path.
// Print CSS in styles.css ensures clean output (hides topbar, forces light, etc).
export function printToPdf(scope?: HTMLElement | null) {
  // The scope param is reserved for future per-card printing; current
  // implementation prints the whole window after print CSS strips chrome.
  void scope;
  window.print();
}
